import {graphNorm} from '@wandb/weave/core';
import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
import * as React from 'react';
import CytoscapeComponent from 'react-cytoscapejs';

import {useColorMode} from '../../../util/useColorMode';
import {useWeaveContext} from '../../context';
import type {ComputeGraphVizProps} from './ComputeGraphViz';
import * as GraphCyto from './graphCyto';

cytoscape.use(dagre);

let renderCount = 0;

const ComputeGraphVizImpl: React.FC<ComputeGraphVizProps> = props => {
  const {node, highlightNodeOrOp, width, height} = props;
  const {isDarkMode} = useColorMode();
  const weave = useWeaveContext();
  const graphColors = isDarkMode
    ? {
        nodeBackground: '#24292f',
        text: '#f0f6fc',
        accent: '#58a6ff',
        edge: '#6e7781',
      }
    : {
        nodeBackground: 'white',
        text: '#24292f',
        accent: '#2e78c7',
        edge: '#99ccdf',
      };

  // Note: this does EXPENSIVE OPERATIONS on every render cycle!
  // Don't use this component in production as is!
  const ng = graphNorm(node);
  const cytoGraph = GraphCyto.normGraphToCyto(
    ng,
    weave.client.opStore,
    highlightNodeOrOp
  );
  // And we use renderCount as a key to force Cytoscape to rerender.
  renderCount++;

  return (
    <CytoscapeComponent
      key={renderCount}
      maxZoom={1.5}
      elements={cytoGraph}
      // If we set height to 100%, or try to use flex, the component starts
      // growing an infinite loop for some reason
      style={{width, height}}
      layout={
        {
          name: 'dagre',
          rankDir: 'LR',
          // rankSep: 10,
          spacingFactor: 0.7,
          nodeDimensionsIncludeLabels: true,
          // cytoscape types don't know about this type because it comes
          // from dagre
        } as any
      }
      stylesheet={[
        {
          selector: 'node[label]',
          style: {
            label: 'data(label)',
            'font-size': '12px',
            'text-wrap': 'ellipsis',
            'text-max-width': '300',
            'text-margin-y': -4,
          },
        },
        {
          selector: 'node',
          style: {
            color: graphColors.text,
            'background-color': graphColors.nodeBackground,
            'border-color': graphColors.accent,
            'border-width': 2,
            width: 20,
            height: 20,
          },
        },
        {
          selector: 'node.highlight',
          style: {
            'background-color': graphColors.accent,
          },
        },
        {
          selector: 'node.const',
          style: {
            shape: 'rectangle',
            width: 30,
            height: 10,
          },
        },
        {
          selector: 'node.var',
          style: {
            shape: 'diamond',
            width: 20,
            height: 20,
          },
        },
        {
          selector: 'node.void',
          style: {
            shape: 'triangle',
            width: 20,
            height: 20,
          },
        },
        {
          selector: 'edge[label]',
          style: {
            label: 'data(label)',
            color: graphColors.text,
            'font-size': '12px',
            'text-wrap': 'ellipsis',
            'text-max-width': '300',
            'text-margin-y': 10,
          },
        },
        {
          selector: 'edge',
          style: {
            width: 2,
            'line-color': graphColors.edge,
            // opacity: 0.5,
            'curve-style': 'straight',
          },
        },
        {
          selector: 'edge',
          style: {
            'target-arrow-shape': 'triangle',
            'target-arrow-color': graphColors.edge,
            // 'arrow-opacity': 0.5,
          },
        },
      ]}
    />
  );
};

export default ComputeGraphVizImpl;
